import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { SignInType, type SignInTokens } from '@bbkAdminRedux/rtkq/auth-types';
import {
  type BbkClientConfig,
  type CbIdpRegion,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import {
  encodeObject,
  encodeObjectForQuery,
} from '@bbkAdminRedux/rtkq/rtkq-utils';
import { JWTParser } from '@bbkAdminUtils/jwt-parser';

export type ChargebeeTokens = { access_token: string };

export type ChargebeeSignInArgs = {
  email?: string;
  jwt?: string;
  cbLoginBaseUrl?: string;
};

export type ChargebeeGetSignInUrlArgs = {
  email?: string;
  redirect?: string;
  cbLoginBaseUrl?: string;
  region?: CbIdpRegion;
};

export class AuthChargebee {
  private config: BbkClientConfig;

  private static BASE_URI = window.location.origin;

  private static LOGIN_REDIRECT_URL = `${AuthChargebee.BASE_URI}${LogInPaths.cbidpExchange}`;

  private static exchangeTokenURL = `/api/v1/cbidp/exchange_token`;

  public static signOutUrl(opts: { redirect?: string } = {}) {
    const { redirect } = opts;
    const query = encodeObjectForQuery({ redirectUrl: redirect });
    return `/api/v1/cbidp/logout?${encodeObject({
      redirect: `${AuthChargebee.BASE_URI}${LogInPaths.signIn}${query}`,
    })}`;
  }

  constructor(config: BbkClientConfig) {
    this.config = config;
  }

  public getSignInUrl({
    email,
    redirect,
    cbLoginBaseUrl,
    region,
  }: ChargebeeGetSignInUrlArgs) {
    const query = encodeObject({
      client_id: this.config.cbidp_client_id,
      forward: `${AuthChargebee.LOGIN_REDIRECT_URL}${encodeObjectForQuery({
        redirect,
        originalEmail: email,
      })}`,
      login_hint: email,
    });
    return `${cbLoginBaseUrl || (region && this.config.cbidp_base_uri[region]) || this.config.cbidp_base_uri.us}/login?${query}`;
  }

  private transformChargebeeTokens(tokens: ChargebeeTokens): SignInTokens {
    const payload = JWTParser.parse<{ exp: number }>(tokens.access_token)!;
    return {
      type: SignInType.CHARGEBEE,
      access_token: tokens.access_token,
      refresh_token: tokens.access_token,
      exp: payload.exp,
    };
  }

  private exchangeJwtCodeForAccessToken = (
    jwt: string
  ): Promise<SignInTokens> =>
    fetch(AuthChargebee.exchangeTokenURL, {
      method: 'POST',
      body: JSON.stringify({ jwt }),
    }).then(async (response) => {
      const result = await response.json();
      if (!response.ok) {
        throw result;
      }
      return this.transformChargebeeTokens(result as ChargebeeTokens);
    });

  public doRefresh = this.exchangeJwtCodeForAccessToken;

  public signIn = async ({
    email,
    jwt,
    cbLoginBaseUrl,
  }: ChargebeeSignInArgs): Promise<SignInTokens> => {
    if (jwt) return this.exchangeJwtCodeForAccessToken(jwt);

    const loginURL = this.getSignInUrl({
      email,
      cbLoginBaseUrl,
      redirect: window.location.pathname + window.location.search,
    });

    window.location.assign(loginURL);
    // FIXME: It's a hack, because window.location.assign redirects to different url
    // I return it just to comply with rtkq contract
    return undefined!;
  };
}
